import { useCallback, useEffect, useState, useMemo } from 'react';
import { Badge, Checkbox, Grid, Box, OutlinedInput, IconButton, Typography, InputAdornment, Button, CircularProgress, Divider, ListItemText, MenuItem, Select, Chip, Avatar } from '@mui/material';
import { motion } from "framer-motion";
import { dashboardCheckout, getDashboardCartItems, deleteDashboardCartItems, addToCart, dashboardAddToCart } from '../../functions/orders';
import { useSelector } from 'react-redux';
import { fetchBranchRooms } from '../../functions/restaurant';
import { getRoomTables } from '../../functions/tables';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme, useMediaQuery } from '@mui/material';
import toast from 'react-hot-toast';
import ModalLayout from '../../components/modals/Layout';
import BackButton from '../../utils/BackButton';
import Header from './Header';
import MenuItems from './Menu';
import Lottie from 'react-lottie';
import CartSkeleton from '../../components/Skeleton/CartSkeleton';
import CartCard from '../../components/Cards/CartCard';
import * as animationData from '../../data/cart.json';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import CloseIcon from '@mui/icons-material/Close';

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice'
  }
};

const useCartItems = (userId) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleGetData = () => {
    setLoading(true);
    getDashboardCartItems(userId).then(res => {
      setCartItems(res.data.data);
    }).finally(() => setLoading(false));
  }

  useEffect(() => { handleGetData() }, [userId]);

  return [cartItems, loading, handleGetData];
};

const RestaurantMenu = () => {
  const navigate = useNavigate();
  const { branch: { currentBranch: { restaurant_code, id: branchId } }, auth: { user: { id: user_id } } } = useSelector(state => state);

  const [searchValue, setSearchValue] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [order_type, setOrder_type] = useState('dine_in');
  const [tables, setTables] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("menu");
  const [deleteAction, setDeleteAction] = useState({ item: null, loading: false, id: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [open, setOpen] = useState(false);

  const [cartItems, loading, handleGetData] = useCartItems(branchId);
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  useEffect(() => {
    fetchBranchRooms(branchId).then(res => {
      setRooms(res.data.data);
    });
  }, [branchId]);


  const handleChange = useCallback((roomId) => {
    getRoomTables(roomId).then(res => {
      setTables(res.data.data);
    });
  }, []);
  useEffect(() => {
    if (order_type === 'takeaway') {
      setSelectedTable(null);
      setTables([]);
    }
  }, [order_type]);
  const tPrice = useMemo(() => cartItems.reduce((acc, item) => acc + item.bill_amount, 0), [cartItems]);
  console.log('Cat Items', cartItems)
  const handlePlaceOrder = useCallback(async () => {
    setCheckoutLoading(true);
    try {
      const cartItemsIds = cartItems.map(cI => ({ id: cI.id }));
      const payload = {
        restaurant_code,
        cart_items: cartItemsIds,
        amount: tPrice,
        order_type,
        ...(order_type === 'dine_in' && {
          table_id: selectedTable
        })
      };
      await dashboardCheckout(payload, branchId);
      navigate("/dashboard/orders");
      toast.success("Your order was successfully created");
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occurred while creating the order");
    } finally {
      setCheckoutLoading(false);
    }
  }, [cartItems, navigate, restaurant_code, selectedTable, tPrice, user_id]);

  const handleRemoveFromCart = useCallback(async () => {
    setDeleteAction(prev => ({ ...prev, loading: true }));
    try {
      await deleteDashboardCartItems(deleteAction.id);
      toast.success("Item deleted successfully!");
      handleGetData();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Error deleting item");
    } finally {
      setIsDeleting(false);
      setDeleteAction({ item: null, loading: false, id: null });
    }
  }, [deleteAction.id, handleGetData]);

  const getRoomValues = useCallback((id) => {
    const room = rooms.find(room => room.id === id);
    return room?.place_name || 'Unknown Room';
  }, [rooms]);

  const getTableValues = useCallback((id) => {
    const table = tables.find(table => table.id === id);
    return table?.table_name || 'Unknown Table';
  }, [tables]);

  const RenderCartUI = useCallback(() => {




    const renderCartItems = () => cartItems.map(({ id, quantity, ordered_addons, food, total_price }) => (
      <CartCard
        key={id}
        id={id}
        cartItem={cartItems?.filter((item) => item?.id === id)}
        food__price={total_price}
        food_image_json={food.food_image_json}
        ordered_addons={ordered_addons}
        food_name={food.food_name}
        quantity={quantity}
        food_code={food.food_code}
        deleteAction={deleteAction}
        setIsDeleting={setIsDeleting}
        setDeleteAction={setDeleteAction}
      // handleQuantity={handleQuantity}
      />
    ));

    const renderEmptyCart = () => (
      <Box
        sx={{
          height: 500,
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center"
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <Lottie options={defaultOptions}
            height={200}
            width={200}
          />
          <Typography sx={{ textAlign: "center" }}>You don't have any items in the cart</Typography>
        </motion.div>
      </Box>
    );

    const renderCartSkeletons = () => (
      <Box sx={{ display: "flex", flexDirection: "column", rowGap: 2, mt: 4 }}>
        {Array(6).fill(0).map((_, index) => <CartSkeleton key={index} />)}
      </Box>
    );

    return (
      <Grid item lg={5} xs={12}>
        <Box display={"flex"} flexDirection={"row"} alignItems={"center"} marginBottom={4}>
          <h2 className='font-semibold text-xl'>Cart Items</h2>
          <BackButton />
        </Box>

        {loading ? renderCartSkeletons() : cartItems.length > 0 ? renderCartItems() : renderEmptyCart()}

        {!loading && cartItems.length > 0 && (
          <>
            <Divider sx={{ mt: 4, mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 1.5 }}>
              <div className='flex flex-row justify-between'>
                <p>( {cartItems.length} items )</p>
                <p className='font-bold text-primary'>
                  {tPrice?.toLocaleString("en", { style: "currency", currency: "KSH" })}
                </p>
              </div>
            </Box>
            <Button
              disabled={cartItems.length === 0}
              variant="contained"
              sx={{ my: 4, width: "100%", color: "white" }}
              onClick={() => setOpen(true)}
            >
              Checkout
            </Button>
          </>
        )}
      </Grid>
    );
  }, [cartItems, loading, deleteAction, setIsDeleting, setDeleteAction, setOpen, tPrice]);

  const renderTabSwitch = () => (
    <Box sx={{ my: 2, display: "flex", columnGap: 2 }}>
      {['menu', 'cart'].map((tab) => (
        <Chip
          key={tab}
          label={`${tab.charAt(0).toUpperCase()}${tab.slice(1)} Items`}
          sx={{
            backgroundColor: activeTab === tab && "#F4B315",
            color: activeTab === tab && "white",
          }}
          onClick={() => setActiveTab(tab)}
          avatar={
            <Avatar sx={{ backgroundColor: 'transparent' }}>
              {tab === 'menu' ?
                <RestaurantIcon sx={{ color: activeTab === tab && "white", fontSize: 18 }} /> :
                <ShoppingCartIcon sx={{ color: activeTab === tab && "white", fontSize: 18 }} />
              }
            </Avatar>
          }
        />
      ))}
    </Box>
  );

  const renderMobileView = () => (
    <Grid container spacing={4}>
      {activeTab === 'menu' && (
        <Grid item lg={7} xs={12} sx={{ display: "flex", flexDirection: "column", mb: 4 }}>
          <OutlinedInput
            sx={{ width: "100%", mt: 2 }}
            size='small'
            placeholder='Search menu items'
            onChange={(e) => setSearchValue(e.target.value)}
            value={searchValue || ''}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={() => setSearchValue(null)}
                  edge="end"
                >
                  {searchValue && <CloseIcon />}
                </IconButton>
              </InputAdornment>
            }
          />
          <Box sx={{ mt: 4 }}>
            <MenuItems count={2} searchValue={searchValue} />
          </Box>
        </Grid>
      )}

      {activeTab === 'cart' && <RenderCartUI />}
    </Grid>
  );

  const renderDesktopView = () => (
    <Grid container spacing={4}>
      <Grid item lg={7} xs={12} sx={{ display: "flex", flexDirection: "column", mb: 4 }}>
        <Header searchValue={searchValue} setSearchValue={setSearchValue} />
        <Box sx={{ mt: 4 }}>
          <MenuItems count={3} searchValue={searchValue} />
        </Box>
      </Grid>
      <RenderCartUI />
    </Grid>
  );

  return (
    <Box sx={{ width: "100%" }}>
      {!isLargeScreen && renderTabSwitch()}
      {!isLargeScreen ? renderMobileView() : renderDesktopView()}

      <ModalLayout open={open} setOpen={setOpen} title="">
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 3 }}>

          {/* ORDER TYPE */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Order Type
            </Typography>

            <Box sx={{ display: "flex", gap: 2 }}>
              {[
                { label: 'Dine In', value: 'dine_in', icon: <RestaurantIcon /> },
                { label: 'Takeaway', value: 'takeaway', icon: <ShoppingCartIcon /> }
              ].map(type => (
                <Chip
                  key={type.value}
                  icon={type.icon}
                  label={type.label}
                  clickable
                  onClick={() => setOrder_type(type.value)}
                  sx={{
                    flex: 1,
                    py: 2,
                    fontWeight: 600,
                    borderRadius: 2,
                    bgcolor: order_type === type.value ? 'primary.main' : 'grey.100',
                    color: order_type === type.value ? 'white' : 'text.primary',
                    '& .MuiChip-icon': {
                      color: order_type === type.value ? 'white' : 'text.secondary'
                    }
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* DINE IN ONLY */}
          {order_type === 'dine_in' && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography variant="subtitle2">
                  Table Selection
                </Typography>

                {/* ROOM (used ONLY to load tables) */}
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  onChange={(e) => handleChange(e.target.value)}
                >
                  <MenuItem value="" disabled>Select dining area</MenuItem>
                  {rooms.map(room => (
                    <MenuItem key={room.id} value={room.id}>
                      {room.place_name}
                    </MenuItem>
                  ))}
                </Select>

                {/* TABLE */}
                <Select
                  fullWidth
                  size="small"
                  displayEmpty
                  value={selectedTable || ''}
                  onChange={(e) => setSelectedTable(e.target.value)}
                  disabled={tables.length === 0}
                >
                  <MenuItem value="" disabled>Select table</MenuItem>
                  {tables.map(table => (
                    <MenuItem key={table.id} value={table.id}>
                      {table.table_name}
                    </MenuItem>
                  ))}
                </Select>

                {tables.length === 0 && (
                  <Typography variant="caption" color="text.secondary">
                    Select a dining area to load available tables
                  </Typography>
                )}
              </Box>
            </motion.div>
          )}

          {/* TAKEAWAY INFO */}
          {order_type === 'takeaway' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'grey.100',
                  fontSize: 14
                }}
              >
                🥡 This order will be prepared for takeaway.
                No table selection required.
              </Box>
            </motion.div>
          )}

          <Divider />

          {/* TOTAL */}
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography fontWeight={600}>Total</Typography>
            <Typography fontWeight={700}>
              {tPrice?.toLocaleString("en", {
                style: "currency",
                currency: "KSH"
              })}
            </Typography>
          </Box>

          {/* ACTION */}
          <Button
            fullWidth
            size="large"
            variant="contained"
            sx={{ color: "white", py: 1.4 }}
            disabled={
              checkoutLoading ||
              cartItems.length === 0 ||
              (order_type === 'dine_in' && !selectedTable)
            }
            onClick={handlePlaceOrder}
          >
            {checkoutLoading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              'Complete Order'
            )}
          </Button>

        </Box>
      </ModalLayout>


      <ModalLayout open={isDeleting} setOpen={setIsDeleting} title={`Delete Cart Item`}>
        <div>
          <p className="my-6">Are you sure you want to delete this cart item?</p>
          <div className="flex gap-4 justify-end">
            <button
              onClick={() => {
                setDeleteAction({ item: null, loading: false, id: null });
                setIsDeleting(false)
              }}
              className="bg-gray-200 px-6 py-2 rounded">Cancel</button>
            <button disabled={deleteAction.loading} onClick={handleRemoveFromCart} className="flex items-center gap-3 bg-red-800 px-6 py-2 text-red-100 rounded">
              Delete {deleteAction.loading && (<CircularProgress color="inherit" size="1.2rem" />)}
            </button>
          </div>
        </div>
      </ModalLayout>
    </Box>
  )
}

export default RestaurantMenu
